import { Link } from "@tanstack/react-router"
import { ArrowLeftStartOnRectangleIcon } from "@heroicons/react/24/outline"
import { useState } from "react"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../dropdown-menu"
import { ProfileImage } from "../profile-image"
import { Skeleton } from "../Skeleton"
import { cn } from "../../../utils/cn"
import { getFullName } from "toptaal-shared"

interface MenuItem {
  label: string
  path: string
  icon: React.ComponentType<{ className?: string }>
}

interface ProfileDropdownProps {
  firstName?: string
  lastName?: string
  isLoading?: boolean
  onLogout: () => void
  menuItems: MenuItem[]
  profilePath?: string
  showProfileImage?: boolean
  variant?: "toptaal" | "organization" | "student"
}

interface TriggerContentProps {
  isOpen: boolean
  showProfileImage: boolean
  isLoading?: boolean
  firstName?: string
  lastName?: string
  variant: "toptaal" | "organization" | "student"
}

function TriggerContent({
  isOpen,
  showProfileImage,
  isLoading,
  firstName,
  lastName,
  variant,
}: TriggerContentProps) {
  return (
    <div
      className={`navigation-menu-link flex flex-row items-center gap-2 py-4 ${
        isOpen ? "dropdown-open" : ""
      }`}
    >
      {showProfileImage && (
        <>
          {isLoading ? (
            <Skeleton className="h-8 w-8 rounded-full" />
          ) : (
            <ProfileImage
              alt="Profiel afbeelding"
              name={getFullName({ firstName: firstName, lastName: lastName })}
              containerClassName={cn({
                "bg-primary-dark-blue": variant === "toptaal",
                "bg-secondary-orange": variant === "organization",
                "bg-secondary-yellow": variant === "student",
              })}
            />
          )}
        </>
      )}

      {firstName && lastName && (
        <>
          {isLoading ? (
            <Skeleton className="h-4 w-24" />
          ) : (
            <>
              {firstName} {lastName}
            </>
          )}
        </>
      )}

      {!showProfileImage && !firstName && !lastName && (
        <div className="h-8 w-8 rounded-full bg-accent" />
      )}
    </div>
  )
}

export function ProfileDropdown({
  firstName,
  lastName,
  isLoading,
  onLogout,
  menuItems = [],
  profilePath,
  showProfileImage = true,
  variant = "toptaal",
}: ProfileDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)

  const handleLogout = () => {
    onLogout()
  }

  return (
    <DropdownMenu onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        {profilePath ? (
          <Link to={profilePath} className="flex flex-row items-center gap-2">
            <TriggerContent
              isOpen={isOpen}
              showProfileImage={showProfileImage}
              isLoading={isLoading}
              firstName={firstName}
              lastName={lastName}
              variant={variant}
            />
          </Link>
        ) : (
          <div className="flex flex-row items-center gap-2 cursor-pointer">
            <TriggerContent
              isOpen={isOpen}
              showProfileImage={showProfileImage}
              isLoading={isLoading}
              firstName={firstName}
              lastName={lastName}
              variant={variant}
            />
          </div>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-full min-w-[200px] max-w-sm"
        align="end"
      >
        {menuItems.map((item, index) => (
          <DropdownMenuItem key={index} asChild>
            <Link to={item.path} className="flex flex-row items-center gap-2">
              <item.icon className="text-primary-dark-blue" />
              {item.label}
            </Link>
          </DropdownMenuItem>
        ))}

        {menuItems.length > 0 && <DropdownMenuSeparator />}

        <DropdownMenuItem onClick={handleLogout} asChild>
          <div className="flex flex-row items-center gap-2">
            <ArrowLeftStartOnRectangleIcon className="text-primary-dark-blue" />
            Uitloggen
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
